import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCheck, Bell, Archive, ArchiveRestore, ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import api from "@/lib/api";
import { format, parseISO, isToday, isYesterday } from "date-fns";
import { fr } from "date-fns/locale";
import { toast } from "sonner";

const TYPE_CLS = {
  validation: "bg-green-50 text-green-700 border-green-200",
  modification: "bg-red-50 text-red-700 border-red-200",
  comment: "bg-blue-50 text-blue-700 border-blue-200",
  info: "bg-neutral-100 text-neutral-600 border-neutral-200",
  rappel_programmation: "bg-orange-50 text-orange-700 border-orange-200",
  planning: "bg-blue-50 text-blue-700 border-blue-200",
  timer: "bg-yellow-50 text-yellow-700 border-yellow-200",
  budget: "bg-orange-50 text-orange-700 border-orange-200",
  rentabilite: "bg-red-50 text-red-700 border-red-200",
  resume: "bg-neutral-100 text-neutral-600 border-neutral-200",
  contenu: "bg-orange-50 text-orange-700 border-orange-200",
  demande: "bg-purple-50 text-purple-700 border-purple-200",
  devis: "bg-blue-50 text-blue-700 border-blue-200",
  rdv: "bg-violet-50 text-violet-700 border-violet-200",
  capacite: "bg-red-50 text-red-700 border-red-200",
  booking: "bg-green-50 text-green-700 border-green-200",
};

const TYPE_LABEL = {
  validation: "Validation",
  modification: "Modification",
  comment: "Commentaire",
  info: "Info",
  rappel_programmation: "Rappel",
  planning: "Planning",
  timer: "Temps",
  budget: "Budget temps",
  rentabilite: "Rentabilité",
  resume: "Résumé",
  contenu: "Contenu",
  demande: "Demande",
  devis: "Devis",
  rdv: "RDV",
  capacite: "Capacité",
  booking: "Réservation",
};

const dayLabel = (iso) => {
  const d = parseISO(iso);
  if (isToday(d)) return "Aujourd'hui";
  if (isYesterday(d)) return "Hier";
  return format(d, "EEEE d MMMM", { locale: fr });
};

const groupByDay = (list) => {
  const groups = [];
  for (const n of list) {
    const label = dayLabel(n.created_at);
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.items.push(n);
    else groups.push({ label, items: [n] });
  }
  return groups;
};

function NotifRow({ n, onArchive, onUnarchive, onClick, archived, selected, onSelect }) {
  const unread = !archived && !n.read;
  return (
    <div
      data-testid={`notif-${n.id}`}
      className={`w-full flex items-start gap-3 px-4 py-3 text-left transition-colors border-l-2 ${
        archived ? "opacity-60 border-l-transparent" : unread ? "border-l-blue-500 bg-blue-50/30" : "border-l-transparent"} ${
        onClick ? "hover:bg-neutral-50 cursor-pointer" : ""}`}
      onClick={onClick}
      role={onClick ? "button" : undefined}
    >
      {!archived && (
        <span onClick={(e) => e.stopPropagation()} className="mt-1 shrink-0">
          <Checkbox
            data-testid={`notif-select-${n.id}`}
            checked={!!selected}
            onCheckedChange={(c) => onSelect?.(n.id, c)}
            aria-label="Sélectionner la notification"
          />
        </span>
      )}
      <div className="flex-1 min-w-0">
        <p className={`text-sm leading-snug ${unread ? "font-medium text-neutral-900" : "text-neutral-500"}`}>{n.text}</p>
        <p className="text-[11px] text-neutral-400 mt-0.5">{format(parseISO(n.created_at), "HH:mm")}</p>
      </div>
      <span className={`text-[10px] rounded-full px-2 py-0.5 border shrink-0 mt-0.5 ${TYPE_CLS[n.type] || TYPE_CLS.info}`}>
        {TYPE_LABEL[n.type] || n.type}
      </span>
      {archived ? (
        <button
          data-testid={`notif-unarchive-${n.id}`}
          onClick={(e) => { e.stopPropagation(); onUnarchive(n); }}
          className="text-neutral-400 hover:text-neutral-900 transition-colors shrink-0 mt-0.5"
          title="Restaurer"
        >
          <ArchiveRestore className="w-4 h-4" />
        </button>
      ) : (
        <button
          data-testid={`notif-archive-${n.id}`}
          onClick={(e) => { e.stopPropagation(); onArchive(n); }}
          className="text-neutral-300 hover:text-neutral-900 transition-colors shrink-0 mt-0.5"
          title="Archiver"
        >
          <Archive className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

export default function NotificationsPage() {
  const [notifs, setNotifs] = useState([]);
  const [showArchives, setShowArchives] = useState(false);
  const [selected, setSelected] = useState([]);
  const [archiving, setArchiving] = useState(false);
  const [filter, setFilter] = useState("all");
  const navigate = useNavigate();

  const load = () => api.get("/notifications").then((r) => setNotifs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const readAll = async () => {
    await api.post("/notifications/read-all");
    window.dispatchEvent(new Event("sf:notifs"));
    load();
  };

  const openNotif = (n) => {
    if (!n.read) {
      api.patch(`/notifications/${n.id}`, { read: true })
        .then(() => window.dispatchEvent(new Event("sf:notifs"))).catch(() => {});
      setNotifs((ns) => ns.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    }
    if (n.publication_id) navigate(`/publications/${n.publication_id}`);
    else if (n.url) navigate(n.url);
  };

  const archive = async (n) => {
    await api.patch(`/notifications/${n.id}`, { archived: true });
    toast.success("Notification archivée");
    load();
  };
  const unarchive = async (n) => {
    await api.patch(`/notifications/${n.id}`, { archived: false });
    toast.success("Notification restaurée");
    load();
  };

  const active = notifs.filter((n) => !n.archived);
  const archived = notifs.filter((n) => n.archived);

  const counts = useMemo(() => {
    const c = {};
    for (const n of active) c[n.type] = (c[n.type] || 0) + 1;
    return c;
  }, [active]);
  const types = Object.keys(counts).sort((a, b) => counts[b] - counts[a]);

  const filtered = filter === "all" ? active : active.filter((n) => n.type === filter);

  const allChecked = filtered.length > 0 && selected.length === filtered.length;
  const toggleAll = (checked) => setSelected(checked ? filtered.map((n) => n.id) : []);
  const toggle = (id, checked) =>
    setSelected((s) => (checked ? [...new Set([...s, id])] : s.filter((x) => x !== id)));

  const archiveSelected = async () => {
    setArchiving(true);
    try {
      for (const id of selected) {
        await api.patch(`/notifications/${id}`, { archived: true });
      }
      toast.success(`${selected.length} notification(s) archivée(s) — retrouvez-les dans « Archives » en bas de page`);
      setSelected([]);
      load();
    } catch { toast.error("Erreur lors de l'archivage"); }
    finally { setArchiving(false); }
  };

  const pickFilter = (t) => { setFilter(t); setSelected([]); };

  return (
    <div className="space-y-4 max-w-2xl" data-testid="notifications-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold">Notifications</h1>
          <p className="text-neutral-500 text-sm mt-1">Validations, commentaires et rappels.</p>
        </div>
        <Button variant="outline" data-testid="read-all-btn" onClick={readAll} className="gap-1.5">
          <CheckCheck className="w-4 h-4" /> Tout marquer lu
        </Button>
      </div>

      {active.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="notif-filters">
          <button data-testid="notif-filter-all" onClick={() => pickFilter("all")}
            className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
              filter === "all" ? "bg-neutral-900 text-white border-neutral-900 font-medium" : "bg-white text-neutral-500 border-neutral-200 hover:border-neutral-400"}`}>
            Toutes ({active.length})
          </button>
          {types.map((t) => (
            <button key={t} data-testid={`notif-filter-${t}`} onClick={() => pickFilter(t)}
              className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                filter === t ? "bg-neutral-900 text-white border-neutral-900 font-medium" : "bg-white text-neutral-500 border-neutral-200 hover:border-neutral-400"}`}>
              {TYPE_LABEL[t] || t} ({counts[t]})
            </button>
          ))}
        </div>
      )}

      <div className="bg-white border border-neutral-200 rounded-xl shadow-sm overflow-hidden">
        {filtered.length > 0 && (
          <div className="flex items-center gap-3 px-4 py-2 border-b border-neutral-100 bg-neutral-50/60">
            <Checkbox data-testid="notif-select-all" checked={allChecked} onCheckedChange={toggleAll} aria-label="Tout sélectionner" />
            <span className="text-xs text-neutral-500">Tout sélectionner</span>
            {selected.length > 0 && (
              <div className="ml-auto flex items-center gap-3 animate-fade-up" data-testid="notif-selection-bar">
                <span className="text-xs font-medium text-neutral-700" data-testid="notif-selection-count">{selected.length} sélectionnée(s)</span>
                <Button size="sm" data-testid="notif-archive-selected" onClick={archiveSelected} disabled={archiving}
                  className="bg-neutral-900 hover:bg-neutral-800 text-white gap-1.5 h-7 text-xs">
                  <Archive className="w-3.5 h-3.5" /> {archiving ? "Archivage…" : "Archiver"}
                </Button>
              </div>
            )}
          </div>
        )}
        {filtered.length === 0 && (
          <div className="p-12 text-center" data-testid="notifications-empty">
            <Bell className="w-8 h-8 mx-auto text-neutral-300 mb-3" />
            <p className="text-sm text-neutral-400">{filter === "all" ? "Aucune notification." : "Aucune notification de ce type."}</p>
          </div>
        )}
        <div className="divide-y divide-neutral-100">
          {groupByDay(filtered).map((g) => (
            <div key={g.label}>
              <p className="px-4 pt-3 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-neutral-400"
                data-testid={`notif-group-${g.label}`}>{g.label}</p>
              {g.items.map((n) => (
                <NotifRow key={n.id} n={n} onArchive={archive} selected={selected.includes(n.id)} onSelect={toggle}
                  onClick={() => openNotif(n)} />
              ))}
            </div>
          ))}
        </div>
      </div>

      {archived.length > 0 && (
        <div className="bg-white border border-neutral-200 rounded-xl shadow-sm overflow-hidden" data-testid="archives-section">
          <button
            data-testid="archives-toggle"
            onClick={() => setShowArchives(!showArchives)}
            className="w-full flex items-center gap-2 px-5 py-3.5 text-left hover:bg-neutral-50 transition-colors"
          >
            {showArchives ? <ChevronDown className="w-4 h-4 text-neutral-400" /> : <ChevronRight className="w-4 h-4 text-neutral-400" />}
            <Archive className="w-4 h-4 text-neutral-400" />
            <span className="font-heading font-bold">Archives</span>
            <span className="ml-auto text-xs text-neutral-400 bg-neutral-100 rounded-full px-2 py-0.5" data-testid="archives-count">{archived.length}</span>
          </button>
          {showArchives && (
            <div className="border-t border-neutral-100 divide-y divide-neutral-100">
              {archived.map((n) => (
                <NotifRow key={n.id} n={n} archived onUnarchive={unarchive} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
